import React, { useState, useEffect } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { FaArrowLeft, FaTimes, FaComments, FaBullseye, FaCheckCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { meetingsApi } from '../../../../services/api/meetingsApi';
import { useAlert } from '../../../../contexts/AlertContext';
import { useAuth } from '../../../../contexts/AuthContext';
import useMeetingsStoreRepository from '../../../../store/meetingsStoreRepository';
import { BackButton } from '../../../common/styles';
import LoadingSpinner from '../../../common/LoadingSpinner';
import EmployeeSearch from '../../../common/EmployeeSearch';
import { EmployeeAvatar } from '../../../common/EmployeeAvatar';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  MainContainer,
} from '../../styles';
import { MEETING_ATTENDEE_TYPES } from '../../../../constants/meetingAttendeeTypes';

const STEPS = ['Basic Info', 'Participants', 'Meeting Type', 'Linked Items', 'Review & Create'];

const MeetingWizard = ({ onBack, onComplete }) => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { user } = useAuth();
  const updateMeeting = useMeetingsStoreRepository((state) => state.updateMeeting);

  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Lookups
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [attendeeTypes, setAttendeeTypes] = useState([]);
  const [meetingStatuses, setMeetingStatuses] = useState([]);

  // Form data
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    scheduledAt: null,
    attendees: [],
    meetingTypeIds: [],
    linkedGoals: [],
    linkedFeedbackResults: [],
  });

  // For clearing employee search after selection
  const [employeeSearchKey, setEmployeeSearchKey] = useState(0);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setIsLoading(true);
      const [types, atTypes, statuses] = await Promise.all([
        meetingsApi.getMeetingTypes(),
        meetingsApi.getAttendeeTypes(),
        meetingsApi.getMeetingStatuses(),
      ]);
      setMeetingTypes(types || []);
      setAttendeeTypes(atTypes || []);
      setMeetingStatuses(statuses || []);
    } catch (err) {
      showAlert('Failed to load initial data', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === STEPS.length - 1) {
      handleCreate();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      onBack();
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleCreate = async () => {
    try {
      setIsCreating(true);

      const meetingData = {
        subject: formData.subject || 'New Meeting',
        description: formData.description,
        scheduledAt: formData.scheduledAt ? formData.scheduledAt.toISOString() : null,
        meetingTypeIds: formData.meetingTypeIds,
      };

      const result = await meetingsApi.createMeeting(meetingData);

      if (result && result.id) {
        // Add attendees if any were selected
        for (const attendee of formData.attendees) {
          try {
            await meetingsApi.addAttendee(result.id, {
              employeeId: attendee.employee.id,
              attendeeTypeId: attendee.attendeeTypeId,
            });
          } catch (err) {
            console.error('Failed to add attendee:', err);
          }
        }

        updateMeeting(result);
        showAlert('Meeting created successfully!', 'success');
        onComplete(result.id);
      } else {
        showAlert('Failed to create meeting', 'error');
      }
    } catch (err) {
      showAlert(err.message || 'Failed to create meeting', 'error', err);
    } finally {
      setIsCreating(false);
    }
  };

  const isNextDisabled = () => {
    switch (activeStep) {
      case 0:
        return !formData.subject.trim();
      case 4:
        return isCreating;
      default:
        return false;
    }
  };

  const handleAddAttendee = (employee) => {
    if (!employee || formData.attendees.some(a => a.employee.id === employee.id)) {
      return;
    }
    const mandatoryType = attendeeTypes.find(t => t.code === MEETING_ATTENDEE_TYPES.MANDATORY) || attendeeTypes[0];
    setFormData(prev => ({
      ...prev,
      attendees: [...prev.attendees, { employee, attendeeTypeId: mandatoryType?.id }]
    }));
    // Reset the search component to clear the selection
    setEmployeeSearchKey(prev => prev + 1);
  };

  const handleRemoveAttendee = (employeeId) => {
    setFormData(prev => ({
      ...prev,
      attendees: prev.attendees.filter(a => a.employee.id !== employeeId)
    }));
  };

  const handleChangeAttendeeType = (employeeId, attendeeTypeId) => {
    setFormData(prev => ({
      ...prev,
      attendees: prev.attendees.map(a =>
        a.employee.id === employeeId
          ? { ...a, attendeeTypeId }
          : a
      )
    }));
  };

  const handleToggleMeetingType = (typeId) => {
    setFormData(prev => ({
      ...prev,
      meetingTypeIds: prev.meetingTypeIds.includes(typeId)
        ? prev.meetingTypeIds.filter(id => id !== typeId)
        : [...prev.meetingTypeIds, typeId]
    }));
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <TextField
              fullWidth
              label="Meeting Subject"
              value={formData.subject}
              onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              margin="normal"
              required
              placeholder="e.g., Weekly 1:1, Career Discussion"
            />
            <TextField
              fullWidth
              label="Description (Optional)"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              margin="normal"
              multiline
              rows={3}
              placeholder="Brief description of the meeting agenda"
            />
            <DateTimePicker
              label="Scheduled Date & Time"
              value={formData.scheduledAt}
              onChange={(value) => setFormData(prev => ({ ...prev, scheduledAt: value }))}
              referenceDate={dayjs()}
              sx={{ width: '100%', mt: 2 }}
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Participants
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You will be added as the Owner automatically. Add other participants below and assign their participation type.
            </Typography>

            {/* Search to add participants */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: '#f8fafc' }}>
              <Typography variant="subtitle2" gutterBottom>
                Add Participant
              </Typography>
              <EmployeeSearch
                key={employeeSearchKey}
                value={null}
                onChange={handleAddAttendee}
                placeholder="Search for employees to add..."
                disabledEmployeeIds={[user?.employee?.id, ...formData.attendees.map(a => a.employee.id)]}
                width={400}
              />
            </Paper>

            {/* Selected participants list */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Selected Participants
                <Chip
                  label={formData.attendees.length + 1}
                  size="small"
                  color="primary"
                  sx={{ height: 20, fontSize: '11px' }}
                />
              </Typography>

              {/* Owner (current user) - always shown */}
              {(() => {
                const ownerType = attendeeTypes.find(t => t.code === MEETING_ATTENDEE_TYPES.OWNER);
                const ownerColor = ownerType?.color || '#22c55e';
                return (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      backgroundColor: `${ownerColor}10`,
                      border: `1px solid ${ownerColor}50`,
                    }}
                  >
                    <EmployeeAvatar employee={user?.employee} size={40} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight={500}>
                        {user?.employee?.fullName} (You)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user?.employee?.email}
                      </Typography>
                    </Box>
                    <Chip
                      label={ownerType?.name || 'Owner'}
                      size="small"
                      sx={{
                        backgroundColor: ownerColor,
                        color: 'white',
                        fontWeight: 500,
                      }}
                    />
                  </Paper>
                );
              })()}

              {/* Other participants */}
              {formData.attendees.map((attendee) => {
                const selectedType = attendeeTypes.find(t => t.id === attendee.attendeeTypeId);
                return (
                  <Paper
                    elevation={0}
                    key={attendee.employee.id}
                    sx={{
                      p: 2,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      backgroundColor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <EmployeeAvatar employee={attendee.employee} size={40} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight={500}>
                        {attendee.employee.fullName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {attendee.employee.email}
                      </Typography>
                    </Box>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={attendee.attendeeTypeId || ''}
                        onChange={(e) => handleChangeAttendeeType(attendee.employee.id, e.target.value)}
                        label="Type"
                      >
                        {attendeeTypes
                          .filter(t => t.code !== MEETING_ATTENDEE_TYPES.OWNER)
                          .map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {type.color && (
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      backgroundColor: type.color,
                                    }}
                                  />
                                )}
                                {type.name}
                              </Box>
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveAttendee(attendee.employee.id)}
                      sx={{ color: '#dc2626' }}
                    >
                      <FaTimes />
                    </IconButton>
                  </Paper>
                );
              })}

              {formData.attendees.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: 'center', fontStyle: 'italic' }}
                >
                  No additional participants added yet. Use the search above to add participants.
                </Typography>
              )}
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Meeting Type
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select one or more meeting types (optional).
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 2,
              }}
            >
              {meetingTypes.map((type) => (
                <Paper
                  elevation={0}
                  key={type.id}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: formData.meetingTypeIds.includes(type.id) ? type.color || '#2196F3' : '#e0e0e0',
                    backgroundColor: formData.meetingTypeIds.includes(type.id) ? `${type.color || '#2196F3'}10` : 'white',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 80,
                    '&:hover': {
                      borderColor: type.color || '#2196F3',
                    }
                  }}
                  onClick={() => handleToggleMeetingType(type.id)}
                >
                  <Typography variant="subtitle1" fontWeight={500}>
                    {type.name}
                  </Typography>
                  {type.description && (
                    <Typography variant="body2" color="text.secondary">
                      {type.description}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
            {meetingTypes.length === 0 && (
              <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No meeting types configured. You can skip this step.
              </Typography>
            )}
          </Box>
        );

      case 3:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Linked Items (Information)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You can link IDP goals, actions, and feedback results after creating the meeting.
            </Typography>
            <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                After the meeting is created, you can link:
              </Typography>
              <Box component="ul" sx={{ margin: 0, paddingLeft: '20px' }}>
                <Typography component="li" variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <FaBullseye style={{ fontSize: 14, color: '#6B7280' }} /> IDP Goals
                </Typography>
                <Typography component="li" variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <FaCheckCircle style={{ fontSize: 14, color: '#6B7280' }} /> IDP Actions
                </Typography>
                <Typography component="li" variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FaComments style={{ fontSize: 14, color: '#6B7280' }} /> Feedback Results
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                Go to the <strong>Discussion</strong> tab and use the menu (<MoreVertIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />) on:
              </Typography>
              <Box component="ul" sx={{ margin: '8px 0', paddingLeft: '20px' }}>
                <Typography component="li" variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <FaComments style={{ fontSize: 14, color: '#6B7280' }} />
                  <strong>Talking Points</strong> - to link items to a specific discussion topic
                </Typography>
                <Typography component="li" variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <DescriptionOutlinedIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                  <strong>Meeting Notes</strong> - to link items at the general meeting level
                </Typography>
              </Box>
            </Paper>
          </Box>
        );

      case 4:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Review Your Selection
            </Typography>

            <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: '#f8fafc' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Subject
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formData.subject || 'Not specified'}
                </Typography>
              </Box>

              {formData.description && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {formData.description}
                  </Typography>
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Scheduled
                </Typography>
                <Typography variant="body1">
                  {formData.scheduledAt
                    ? dayjs(formData.scheduledAt).format('DD/MM/YYYY [at] HH:mm')
                    : 'Not scheduled'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Participants ({formData.attendees.length + 1})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                  {(() => {
                    const ownerType = attendeeTypes.find(t => t.code === MEETING_ATTENDEE_TYPES.OWNER);
                    const ownerColor = ownerType?.color || '#22c55e';
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <EmployeeAvatar employee={user?.employee} size={28} />
                        <Typography variant="body1">{user?.employee?.fullName}</Typography>
                        <Chip label={ownerType?.name || 'Owner'} size="small" sx={{ height: 20, fontSize: '11px', backgroundColor: ownerColor, color: 'white' }} />
                      </Box>
                    );
                  })()}
                  {formData.attendees.map(a => {
                    const type = attendeeTypes.find(t => t.id === a.attendeeTypeId);
                    return (
                      <Box key={a.employee.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <EmployeeAvatar employee={a.employee} size={28} />
                        <Typography variant="body1">{a.employee.fullName}</Typography>
                        {type && (
                          <Chip
                            label={type.name}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '11px',
                              backgroundColor: type.color || '#6b7280',
                              color: 'white',
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {formData.meetingTypeIds.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Meeting Types
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {formData.meetingTypeIds.map(id => {
                      const type = meetingTypes.find(t => t.id === id);
                      if (!type) return null;
                      return (
                        <Chip
                          key={id}
                          label={type.name}
                          size="small"
                          sx={{
                            backgroundColor: type.color || '#6b7280',
                            color: 'white',
                            fontWeight: 500,
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Paper>

            <Typography variant="body2" color="text.secondary">
              Click "Create Meeting" to create the meeting. You'll be able to add
              talking points, notes, and action items afterward.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  if (isLoading && meetingTypes.length === 0) {
    return <LoadingSpinner message="Loading..." />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={onBack}>
            <FaArrowLeft /> Back to Meetings
          </BackButton>
          <Title>Create Meeting</Title>
        </HeaderLeft>
      </Header>

      <MainContainer>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 900,
            mx: 'auto',
            p: 4,
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            backgroundColor: 'white',
          }}
        >
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ minHeight: 300 }}>
            {renderStepContent(activeStep)}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 3, borderTop: '1px solid #e2e8f0', mt: 3 }}>
            <Button onClick={handleBack} sx={{ mr: 1 }}>
              {activeStep === 0 ? 'Cancel' : 'Back'}
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isNextDisabled()}
            >
              {activeStep === STEPS.length - 1 ? (
                isCreating ? 'Creating...' : 'Create Meeting'
              ) : (
                'Next'
              )}
            </Button>
          </Box>
        </Paper>
      </MainContainer>
    </Container>
  );
};

export default MeetingWizard;
